/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ApiaUtil,
  ApiaUtilModalHandler,
  TOpenModal,
  useModalContext,
} from '@apia/components';
import { getLabel } from '@apia/util';
import { Box, Button, getVariant } from '@apia/theme';
import { hasSucceedFormValidation, initValidations } from '../util';
import { Form } from '../form/Form';
import { CollectorField } from './CollectorField';
import { CollectorCheckbox } from './fields/Checkbox';
import { CollectorInput } from './fields/Input';
import { CollectorSelect } from './fields/Select';
import { CollectorTextarea } from './fields/Textarea';
import { CollectorLabel } from './fields/Label';
import { CollectorButton } from './fields/Button';
import { ValidatedCollectorField } from './ValidatedCollectorField';

try {
  void initValidations().catch(() => {});
} catch (e) {
  console.error(e);
}

let maxId = 0;

export class Collector {
  fields = {
    button: CollectorButton,
    checkbox: CollectorCheckbox,
    input: CollectorInput,
    /**
     * Muestra una etiqueta con tag personalizable: h1...h6, p, label. (Por
     * defecto: label)
     */
    label: CollectorLabel,
    select: CollectorSelect,
    textarea: CollectorTextarea,
  };

  private private__id = `Collector${maxId++}`;
  public get id() {
    return this.private__id;
  }
  modal: ApiaUtilModalHandler | null = null;

  #fields: Record<string, CollectorField> = {};

  add = (...fields: CollectorField[]) => {
    fields.forEach((field) => {
      this.#fields[field.name] = field;
      // eslint-disable-next-line no-param-reassign
      field.collector = this;
    });
    return this;
  };

  forEach = (cb: (field: CollectorField, i: number) => void) => {
    Object.values(this.#fields).forEach(cb);
  };

  #Component = ({
    hideConfirm,
    resolve,
  }: {
    hideConfirm?: boolean;
    resolve: (v: Record<string, unknown>) => boolean;
  }) => {
    const modal = useModalContext();

    let hasValidated = false;

    return (
      <Box
        className="formContainer"
        {...getVariant('layout.util.collector.formContainer')}
      >
        <Form
          name={this.private__id}
          onValidate={(result) => {
            if (hasSucceedFormValidation(result)) {
              const res = resolve(result.submitValues);
              if (res) {
                modal.close();
              }
            }
          }}
        >
          <Box className="collector__fields">
            {Object.values(this.#fields).map((current) => {
              if (current instanceof ValidatedCollectorField)
                hasValidated = true;

              return <current.SuperComponent key={current.name} />;
            })}
          </Box>
          {hasValidated && !hideConfirm && (
            <Box className="collector__buttons">
              <Button variant="outline">{getLabel('btnCon').text}</Button>
            </Box>
          )}
        </Form>
      </Box>
    );
  };

  show = <FormValues extends Record<string, unknown>>(
    modalProps?: Partial<
      Omit<TOpenModal, 'children' | 'onConfirm' | 'onCancel' | 'confirmProps'>
    > &
      Partial<{
        hideConfirm: boolean;
        /**
         * Si este método devuelve false, el modal no se cerrará
         */
        onConfirm?: (res: FormValues | null) => boolean;
      }>,
  ): Promise<FormValues | null> => {
    const { onConfirm, ...actualModalProps } = modalProps ?? {};
    const hasResolved = false;
    return new Promise<FormValues | null>((resolve) => {
      const C = this.#Component;
      const modal = ApiaUtil.instance.modals.open({
        onExited: () => {
          if (!hasResolved) {
            this.modal = null;
            resolve(null);
          }
        },
        children: (
          <C
            hideConfirm={modalProps?.hideConfirm}
            resolve={(values) => {
              if (onConfirm) {
                const confirmResult = onConfirm(values as FormValues);
                if (!confirmResult) {
                  return false;
                }
              }

              resolve(values as FormValues);
              return true;
            }}
          />
        ),
        variant: 'layout.util.collector.modal',
        initialFocusGetter(modalInnerRef) {
          return modalInnerRef.querySelector<HTMLElement>(
            'input,textarea,select,button:not(.modal__closeButton)',
          );
        },
        ...actualModalProps,
      });
      this.modal = modal;
    });
  };
}
